import { useMemo } from "react";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import { PreviewRowKind, PreviewStatementDataset } from "./previewTypes";
import { filterStatementRows } from "./filterPreviewRows";
import { previewRowPercent } from "./previewPercent";
import { AmountCell } from "./AmountCell";
import { PercentCell } from "./PercentCell";
import styles from "./ReportPreview.module.scss";

interface Props {
    dataset: PreviewStatementDataset;
    numberFormat: NumberFormatValues;
    rowVisibility: ReportSettingRowVisibility;
}

const rowClassName: Record<PreviewRowKind, string> = {
    section: styles.sectionRow,
    line: styles.lineRow,
    total: styles.totalRow,
    spacer: styles.spacerRow,
};

export const StatementTable = ({
    dataset,
    numberFormat,
    rowVisibility,
}: Props) => {
    const rows = useMemo(
        () => filterStatementRows(dataset.rows, rowVisibility),
        [dataset.rows, rowVisibility],
    );

    return (
        <div className={styles.table}>
            <div className={styles.accountHeader}>
                <span>{dataset.accountHeader}</span>
                <span className={styles.rightGroup}>
                    <span className={styles.percentCell}>
                        {dataset.percentHeader}
                    </span>
                    <span className={styles.amountCell}>
                        {formatMoneyColumnHeader(
                            dataset.amountHeader,
                            numberFormat,
                        )}
                    </span>
                </span>
            </div>
            {rows.map((row) => {
                if (row.kind === "spacer") {
                    return (
                        <div key={row.id} className={rowClassName[row.kind]} />
                    );
                }
                const percent = previewRowPercent({
                    amount: row.amount,
                    percentBase: dataset.percentBase,
                });
                return (
                    <div
                        key={row.id}
                        className={rowClassName[row.kind]}
                        data-top-border={row.hasTopBorder || undefined}
                    >
                        <span>{row.label}</span>
                        {row.amount !== undefined && (
                            <span className={styles.rightGroup}>
                                <PercentCell
                                    value={percent}
                                    numberFormat={numberFormat}
                                />
                                <AmountCell
                                    value={row.amount}
                                    numberFormat={numberFormat}
                                />
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
